/**
 * spiceKindColorsApi: the owner's write of the two SPICE session kind
 * colours.
 *
 *   PUT /api/t/{slug}/v1/admin/spice/kind-colors   (manage_team)
 *
 * There is no read route: every seat reads the colours from the tenant
 * config (useSpiceKindColors). A save invalidates that query, so this
 * session sees the new colours at once; other open sessions see them
 * when their tenant config next refreshes (within ten minutes) or on
 * their next page load.
 */

import { writeFetch } from './writeFetch';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ApiError } from './api';
import type { SpiceSessionKind } from './spiceVisitPlansApi';

export type SpiceKindColorsBody = Record<SpiceSessionKind, string>;

export function useSetSpiceKindColors(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (colors: SpiceKindColorsBody): Promise<SpiceKindColorsBody> => {
      const url = `/api/t/${encodeURIComponent(slug)}/v1/admin/spice/kind-colors`;
      const res = await writeFetch(url, {
        method: 'PUT',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify({ spice: colors.spice, spice_friendly_visitor: colors.spice_friendly_visitor }),
      });
      if (!res.ok) {
        let body: unknown;
        try {
          body = await res.json();
        } catch {
          body = null;
        }
        throw new ApiError(res.status, body, `PUT ${url} → HTTP ${res.status}`);
      }
      return (await res.json()) as SpiceKindColorsBody;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['tenant-config', slug] });
    },
  });
}
